import AddRounded from '@mui/icons-material/AddRounded';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Edit from '@mui/icons-material/Edit';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { ButtonLink } from '@src/common/components/button/ButtonLink';
import { FilterableTable } from '@src/common/components/FilterableTable';
import type { Filter } from '@src/common/components/Filters';
import { IconButton } from '@src/common/components/IconButton';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { PermitType } from '@src/orders/domain/PermitType';
import { usePermitTypes } from '@src/orders/hooks/usePermitTypes';
import type { PermitTypeFilters } from '@src/orders/services/PermitTypesClient';
import { usePermitTypeDeactivation } from '@src/permitTypesManager/hooks/usePermitTypeDeactivation';
import { Navigation } from '@src/routing/Navigation';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const PermitTypesManager = () => {
  const { t } = useTranslation();

  const { filteredPermitTypes, permitTypes, filters } = usePermitTypes();

  return (
    <PageLayout
      title={t('permitTypesManager.title')}
      rightBlock={
        <ButtonLink
          to={Navigation.permitTypesManager.addType.PATH}
          startIcon={<AddRounded />}
        >
          {t('permitTypesManager.addPermitType.navigationName')}
        </ButtonLink>
      }
      style={{ margin: 0 }}
    >
      <PermitTypeTable
        permitTypes={
          filteredPermitTypes
            ? filteredPermitTypes.permitTypes
            : permitTypes.permitTypes
        }
        filters={filters}
      />
    </PageLayout>
  );
};

interface Props {
  permitTypes: PermitType[];
  filters: {
    filters: Filter<PermitTypeFilters>[];
    onChange: (filters: PermitTypeFilters) => void;
  };
}

const PermitTypeTable = ({
  permitTypes,
  filters: { onChange, filters },
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deactivateType, reactivateType, isLoading } =
    usePermitTypeDeactivation();

  const navigateToEditionPage = (permitTypeId: string) => {
    navigate(Navigation.permitTypesManager.editType.url(permitTypeId));
  };

  const navigateToDuplicationPage = (permitTypeId: string) => {
    navigate(Navigation.permitTypesManager.addType.url(permitTypeId));
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('permitType.name'), flex: 1 },
    { field: 'type', headerName: t('permitType.type'), flex: 1 },
    {
      field: 'jurisdiction',
      headerName: t('permitType.jurisdiction'),
      flex: 1,
    },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <Stack direction="row" flex={1}>
            <IconButton
              onClick={() => navigateToEditionPage(params.row.id)}
              title={t('edit')}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => navigateToDuplicationPage(params.row.id)}
              title={t('permitTypesManager.duplicate')}
            >
              <ContentCopy />
            </IconButton>
            <IconButton
              disabled={isLoading}
              onClick={() =>
                params.row.isActive
                  ? deactivateType(params.row.id)
                  : reactivateType(params.row.id)
              }
              title={
                params.row.isActive
                  ? t('permitTypesManager.deactivate')
                  : t('permitTypesManager.reactivate')
              }
            >
              {params.row.isActive ? (
                <BlockIcon color="error" />
              ) : (
                <RestoreRounded color="secondary" />
              )}
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const rows = permitTypes.map((permitType) => ({
    id: permitType.id,
    name: permitType.name,
    type: permitType.type,
    jurisdiction: permitType.jurisdiction,
    isActive: permitType.isActive,
  }));

  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <FilterableTable
        columns={columns}
        rows={rows}
        ariaLabel={t('permitTypeTable')}
        paginationAndSortMode="client"
        filters={{
          filters,
          onChange,
        }}
      />
    </Stack>
  );
};
